/**
 * Tile Configuration for the Grid-Based Layout System
 * 
 * The deck builder uses a 10-unit grid system:
 * - Default: Deck Area (7 units) | Sidebar Tiles (3 units)
 * - Expanded: Deck Area (4 units) | Sidebar Tiles (6 units)
 * - Collapsed: Deck Area (9 units) | Collapsed tiles (1 unit as icons)
 * 
 * Each tile has configurable height units for responsive layouts.
 */

// Grid configuration
export const GRID_CONFIG = {
  totalColumns: 10,
  layouts: {
    default: {
      deckArea: 7,
      tileArea: 3,
    },
    expanded: {
      deckArea: 4,
      tileArea: 6,
    },
  },
  // Base height unit in pixels for calculating tile heights
  heightUnit: 60,
  // Gap between tiles in pixels
  gap: 12,
};

// Tile definitions with their configurations
export const TILE_CONFIG = {
  deckStats: {
    id: 'deckStats',
    icon: '📊',
    titleKey: 'sidebar.stats.title',
    defaultTitle: 'Deck Stats',
    defaultHeight: 'auto',  // Auto-expand to fit content
    minHeight: 1,           // Minimum height (collapsed state)
    maxHeight: 12,          // Allow larger expansion for charts
    defaultCollapsed: false, // Start expanded
    order: 1,               // Display order in the tile grid
    // Deck stats can expand to fill available space when not collapsed
    canFill: true,
  },
  cardSearch: {
    id: 'cardSearch',
    icon: '🔍',
    titleKey: 'sidebar.search.title',
    defaultTitle: 'Card Search',
    defaultHeight: 'auto', // Auto-fill available space
    minHeight: 1, // Collapsed height should be minimal
    maxHeight: 12,
    defaultCollapsed: false,
    order: 2,
    // Card search is the primary tile - takes remaining space
    isPrimary: true,
  },
  recommendations: {
    id: 'recommendations',
    icon: '💡',
    titleKey: 'sidebar.recommendations.title',
    defaultTitle: 'Recommendations',
    defaultHeight: 2,
    minHeight: 1,
    maxHeight: 4,
    defaultCollapsed: true,
    order: 3,
  },
  drawSimulator: {
    id: 'drawSimulator',
    icon: '🎴',
    titleKey: 'sidebar.drawSimulator.title',
    defaultTitle: 'Draw Simulator',
    defaultHeight: 2,
    minHeight: 1,
    maxHeight: 4,
    defaultCollapsed: true,
    order: 4,
  },
  preloadDecks: {
    id: 'preloadDecks',
    icon: '📁',
    titleKey: 'sidebar.sampleDecks.title',
    defaultTitle: 'Sample Decks',
    defaultHeight: 'auto',
    minHeight: 1,
    maxHeight: 12,
    defaultCollapsed: true,
    order: 5,
    fillRemaining: true,
  },
};

// Get ordered array of tile configs
export const getOrderedTiles = () => {
  return Object.values(TILE_CONFIG).sort((a, b) => a.order - b.order);
};

// Calculate grid template columns based on layout mode
export const getGridTemplateColumns = () => {
  return `repeat(${GRID_CONFIG.totalColumns}, 1fr)`;
};

// Get column span for deck area based on layout mode
export const getDeckAreaSpan = (layoutMode = 'default') => {
  const layouts = GRID_CONFIG.layouts;
  return (layouts[layoutMode] || layouts.default).deckArea;
};

// Get column span for tile area based on layout mode
export const getTileAreaSpan = (layoutMode = 'default') => {
  const layouts = GRID_CONFIG.layouts;
  return (layouts[layoutMode] || layouts.default).tileArea;
};

export default TILE_CONFIG;
